import { ActionButton, ActionButtonIntent, ActionButtonSize, Modal } from "widgets";
import { useOverlay } from "widgets/hooks";

import type { Dataset } from "../../graphql.js";
import { Tile } from "../../modules/core/components/Tile.js";
import { FILE_BROWSE_TYPES, FILE_DISPLAY_NAME } from "../../settings/settings.js";
import { FileIcon } from "../core/FileIcon.js";
import DatasetPickerModalDialog from "./DatasetPickerModalDialog.js";

interface IDatasetPickerProps {
  isLoading?: boolean;
  label: string;
  selectedDatasetId: number;
  showMissingDatasetTooltip?: boolean; // support for guidance tooltips e.g. in data viewer
  datasets: Dataset[];
  handleSelectedDatasetChanged: (dataset: Dataset) => void;
  globalContext?: boolean;
}

function DatasetPicker(props: IDatasetPickerProps) {
  const { triggerProps, overlayProps, state } = useOverlay({});

  const selectedDataset: Dataset =
    props.selectedDatasetId && props.datasets
      ? props.datasets.find((d: Dataset) => d.id === props.selectedDatasetId)
      : null;

  function onDatasetModalApply(datasetIds: number[]) {
    // currently only single datasets can be selected
    const datasetId = datasetIds.length > 0 ? datasetIds[0] : null;
    // only execute callback if the selected data set changed
    if (datasetId !== props.selectedDatasetId) {
      props.handleSelectedDatasetChanged(datasetId ? props.datasets.find((d: Dataset) => d.id === datasetId) : null);
      state.setOpen(false);
      return Promise.resolve(true);
    } else {
      state.setOpen(false);
    }
  }

  // This is a very much simplified version of the Data Viewer dataset selection widget, using a new dialog component.
  // The widget is likely replaced by a visually more appealing version later.
  return (
    <div className="chart-dataset-selector selector-component">
      <div className="form-group">
        <span id="data-viewer-dataset-selection" className="hotspot-beacon" />
        <label className="control-label">{props.label}</label>

        {selectedDataset ? (
          <>
            <ActionButton
              {...triggerProps}
              size={ActionButtonSize.Small}
              label={"Select"}
              style={{ float: "right" }}
              icon={"description"}
              intent={ActionButtonIntent.Secondary}
            />
            <Tile
              title={selectedDataset.name}
              icon={<FileIcon filename={selectedDataset.name} size={"big"} />}
              statusDisplayName={FILE_DISPLAY_NAME[FILE_BROWSE_TYPES.REGULAR_FILE]}
              onPress={() => state.setOpen(true)}
            />
          </>
        ) : (
          <div style={{ display: "flex", justifyContent: "center", marginTop: 10 }}>
            <ActionButton
              {...triggerProps}
              label={"Select a Dataset"}
              icon={"description"}
              isLoading={props.isLoading}
            />
          </div>
        )}
      </div>
      <Modal state={state} {...overlayProps}>
        <DatasetPickerModalDialog
          datasets={props.datasets}
          selectedDatasetIds={props.selectedDatasetId ? [props.selectedDatasetId] : []}
          exclusiveSelection={true}
          onSelect={onDatasetModalApply}
          onCancel={state.close}
          globalContext={props.globalContext}
        />
      </Modal>
    </div>
  );
}

export { DatasetPicker };
